import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';

import { MotivoAfastamentoService } from '@/core/api/sicom-api/sistema/motivo-afastamento/motivo-afastamento.service';
import { MotivoAfastamentoResponse } from '@/core/api/sicom-api/sistema/motivo-afastamento/motivo-afastamento.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-motivo-afastamento-detalhe',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, DialogModule, ButtonModule, InputTextModule, CanDirective],
    templateUrl: './motivo-afastamento-detalhe.html'
})
/** Diálogo de novo/edição de motivo de afastamento (código + descrição) com upsert e emissão de "salvo". */
export class MotivoAfastamentoDetalhe {
    private readonly fb = inject(FormBuilder);
    private readonly service = inject(MotivoAfastamentoService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);

    /** Visibilidade do modal (two-way). */
    readonly visivel = model<boolean>(false);
    /** Motivo em edição, ou `null` para cadastro novo. */
    readonly motivo = input<MotivoAfastamentoResponse | null>(null);
    /** Emite após salvar com sucesso (para a lista recarregar). */
    readonly salvo = output<void>();

    protected readonly roleMotivoEditar = PERMISSOES.configuracoes.motivoAfastamento.editar;

    protected readonly salvando = signal(false);

    private codigo: number | null = null;

    protected readonly form = this.fb.nonNullable.group({
        descricao: ['', [Validators.required, Validators.maxLength(50)]]
    });

    constructor() {
        // Preenche (edição) ou reseta (novo) sempre que o modal abre.
        effect(() => {
            if (!this.visivel()) {
                return;
            }
            const item = this.motivo();
            if (item) {
                this.codigo = item.codigo ?? null;
                this.form.reset({ descricao: item.descricao ?? '' });
            } else {
                this.codigo = null;
                this.form.reset({ descricao: '' });
            }
        });
    }

    protected get tituloModal(): string {
        return this.motivo() ? 'Editar motivo de afastamento' : 'Novo motivo de afastamento';
    }

    protected salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        this.salvando.set(true);
        const v = this.form.getRawValue();
        this.service
            .salvar({ codigo: this.codigo ?? undefined, descricao: v.descricao.trim() })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Motivo salvo', detail: 'O motivo de afastamento foi salvo com sucesso.' });
                    this.salvando.set(false);
                    this.salvo.emit();
                    this.visivel.set(false);
                },
                error: () => {
                    this.salvando.set(false);
                }
            });
    }

    protected fechar(): void {
        this.visivel.set(false);
    }
}
